import React, { useEffect, useMemo, useState } from 'react';
import NewFolder from '../../ActionBar/NewFolder';
import RefreshFiles from '../../ActionBar/RefreshFiles';
import styled from '@emotion/styled';
import { Divider, Grid, TextField, Typography, InputAdornment, darken, lighten, IconButton } from '@mui/material';
import {
  selectCurrentPath,
  selectFileManagerIsLoading,
  selectFileResultsTotal,
  selectFileSearchParams
} from '@/redux/selectors/fileManagerSelectors';
import { getFolder, setSearchParams } from '@/redux/slices/fileManagerSlice';
import PolarisIcon from '@/components/_common/PolarisIcon';
import ClearSearchButton from '@/components/_common/IconButtons/ClearSearchButton';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';

interface Props {
  canCreateFolder?: boolean;
}

const FolderBar: React.FC<Props> = ({ canCreateFolder = true }) => {
  const dispatch = useAppDispatch();
  const [searchValue, setSearchValue] = useState<string>('');
  const currentPath = useAppSelector(selectCurrentPath) || [];
  const searchParams = useAppSelector(selectFileSearchParams);
  const resultsTotal = useAppSelector(selectFileResultsTotal);
  const isLoading = useAppSelector(selectFileManagerIsLoading);
  const folderFullPath = useMemo(() => {
    let fullPath: string[] = [];
    if (currentPath && Array.isArray(currentPath) && currentPath.length > 0) {
      fullPath = currentPath;
    }
    return fullPath;
  }, [currentPath]);

  const searchHistory = localStorage.getItem('search-history');
  const [fileHistory, setFileHistory] = useState<string[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const searchHistoryLengthMax = 5;
  let skipBlur = false;

  useEffect(() => {
    if (!searchHistory) {
      localStorage.setItem('search-history', JSON.stringify({ 'file-manager': [] }));
    } else {
      setFileHistory(JSON.parse(searchHistory)['file-manager']);
    }
  }, []);

  useEffect(() => {
    if (!searchParams.prefix_search?.length) {
      setSearchValue('');
    }
  }, [searchParams.prefix_search]);

  const navigateFolderPath = () => {
    dispatch(getFolder({ path: folderFullPath }));
  };

  const setAllFileHistory = (updated: string[]) => {
    localStorage.setItem(
      'search-history',
      JSON.stringify({
        ...JSON.parse(searchHistory || ''),
        'file-manager': updated
      })
    );
    setFileHistory(updated);
  };

  const handleSubmitSearch = async (param: React.FormEvent<HTMLFormElement> | string) => {
    // if param is a form event, it was manually entered - if its a string, it was selected from the search history
    if (param && typeof param !== 'string') param.preventDefault();

    await dispatch(setSearchParams({ prefix_search: typeof param === 'string' ? param : searchValue }));
    navigateFolderPath();

    if (param && typeof param !== 'string') {
      let newSearchHistory = [searchValue].concat(fileHistory);

      if (fileHistory.includes(searchValue)) {
        newSearchHistory = [searchValue].concat(fileHistory.filter((e) => e.trim() !== searchValue.trim()));
      }

      setAllFileHistory(newSearchHistory.slice(0, searchHistoryLengthMax));
      setShowHistory(false);
    }
  };

  const handleClearSearch = async () => {
    setSearchValue('');
    await dispatch(setSearchParams({ prefix_search: '' }));
    navigateFolderPath();
  };

  const removeFromSearchHistory = (value: string, index: number) => {
    const newHistory = [...fileHistory];
    newHistory.splice(index, 1);
    setAllFileHistory(newHistory);
    setSearchValue('');

    skipBlur = true;
  };

  const applySearchFromHistory = (value: string, index: number) => {
    setSearchValue(value);
    handleSubmitSearch(value);

    // moves the search value to the top of the search history
    const newHistory = [...fileHistory];
    const element = fileHistory[index];
    newHistory.splice(index, 1);
    newHistory.unshift(element);
    setAllFileHistory(newHistory);

    skipBlur = true;
    setShowHistory(false);
  };

  const resetSearchHistory = () => {
    setAllFileHistory([]);
  };

  const handleBlur = () => {
    // timeout so searches can apply before disappearing
    setTimeout(() => {
      if (skipBlur) {
        skipBlur = false;
      } else {
        setShowHistory(false);
      }
    }, 200);
  };

  const hasSearch = searchParams.prefix_search?.length;

  return (
    <FolderBarWrapper>
      <Grid container alignItems="center" sx={{ minHeight: 53 }}>
        <form onSubmit={handleSubmitSearch} style={{ position: 'relative' }} autoComplete="off">
          <SearchField
            hiddenLabel
            onFocus={() => setShowHistory(true)}
            onBlur={handleBlur}
            variant="filled"
            size="small"
            disabled={isLoading}
            placeholder="Search for name beginning with..."
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            hasvalue={+searchValue?.length}
            multiline={false}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon icon="search" height={18} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">{searchValue?.length > 0 && <ClearSearchButton onClick={handleClearSearch} />}</InputAdornment>
              )
            }}
          />
          {showHistory && fileHistory.length > 0 && (
            <HistoryDropdown>
              {fileHistory.map((search: string, i: number) => (
                <HistoryOption key={`option-${i + 1}`} value={search} index={i} onRemove={removeFromSearchHistory} onClick={applySearchFromHistory} />
              ))}
              <ClearHistory>
                <span onClick={() => resetSearchHistory()}>Clear all</span>
              </ClearHistory>
            </HistoryDropdown>
          )}
        </form>

        <Typography sx={{ fontWeight: 'normal', ml: 'auto', opacity: hasSearch ? 1 : 0.5, color: hasSearch ? 'primary.main' : 'text.secondary' }}>
          <strong>{resultsTotal}</strong> file{resultsTotal > 1 && 's'}
        </Typography>
        <Divider orientation="vertical" flexItem sx={{ mx: 2, ml: 3, opacity: 0.5 }} />
        <Grid item style={{ display: 'flex', flexDirection: 'row' }}>
          {canCreateFolder && <NewFolder />}
          <RefreshFiles />
        </Grid>
      </Grid>
    </FolderBarWrapper>
  );
};

type HistoryOptionProps = {
  value: string;
  index: number;
  onRemove: (value: string, index: number) => void;
  onClick: (value: string, index: number) => void;
};

const HistoryOption = ({ value, index, onRemove, onClick }: HistoryOptionProps) => {
  return (
    <Option>
      <ApplySearchArea onClick={() => onClick(value, index)}>
        <PolarisIcon
          icon="history"
          height={18}
          width={18}
          style={{
            opacity: 0.5,
            minWidth: 18,
            minHeight: 18
          }}
        />
        <Value>{value}</Value>
      </ApplySearchArea>
      <IconButton
        onClick={() => onRemove(value, index)}
        style={{
          cursor: 'pointer',
          padding: '2px',
          minWidth: 18,
          minHeight: 18
        }}
      >
        <PolarisIcon className="remove" icon="close" height={18} width={18} />
      </IconButton>
    </Option>
  );
};

export default FolderBar;

const ApplySearchArea = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  column-gap: ${({ theme }) => theme.spacing(1)};
  flex-grow: 1;
  width: 100%;
  cursor: pointer;
`;

const FolderBarWrapper = styled.div`
  position: sticky;
  left: 0;
  top: 0;
  right: 0;
  z-index: 100;
  width: 100%;
  background: ${({ theme }) => darken(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.2 : 0.02)};
  border: 1px solid ${({ theme }) => darken(theme.palette.divider, 0.4)};
  border-left: 0;
  border-top: 0;
  padding: ${({ theme }) => theme.spacing(0, 2)};
`;

const SearchIcon = styled(PolarisIcon)`
  color: ${({ theme }) => theme.palette.text.secondary};
`;

const SearchField = styled(TextField)<{ hasvalue: number }>`
  min-width: 270px;
  padding: ${({ theme }) => theme.spacing(1, 0)};
  transition: opacity 0.2s ease-in-out;

  .MuiInputBase-root {
    border: 1px solid transparent;
    padding: ${({ theme }) => theme.spacing(0, 0.75, 0, 1.25)};
    opacity: 0.5;
    &:hover {
      opacity: 0.7;
      transition: opacity 0.2s ease-in-out;
    }
    &.Mui-focused {
      opacity: 1;
      transition: opacity 0.2s ease-in-out;
    }
  }
  ${({ theme, hasvalue }) =>
    hasvalue &&
    `
    .MuiInputBase-root {
      border: 1px solid ${darken(theme.palette.primary.main, 0.4)};
      opacity: 1;
    }
  `}
`;

const HistoryDropdown = styled.div`
  position: absolute;
  width: 100px;
  background: black;
  top: 47px;
  width: 100%;
  padding-top: ${({ theme }) => theme.spacing(1)};
  background: ${({ theme }) =>
    theme.palette.mode === 'dark' ? lighten(theme.palette.background.paper, 0.05) : darken(theme.palette.background.paper, 0.05)};
`;

const ClearHistory = styled.div`
  padding: ${({ theme }) => theme.spacing(0.5, 1, 1.3, 1.5)};
  font-size: 0.8rem;
  font-style: italic;
  opacity: 0.5;
  cursor: arrow;

  span {
    cursor: pointer;
    &:hover {
      text-decoration: underline;
    }
  }
`;

const Option = styled.div`
  display: flex;
  align-items: center;
  flex-direction: row;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing(0.75, 1.5)};
  column-gap: ${({ theme }) => theme.spacing(1.5)};
  min-height: 35px;

  .remove {
    display: none;
    opacity: 0.5;

    &:hover {
      opacity: 1;
      background-color: rgba(255, 255, 255, 0.2);
    }
  }

  &:hover {
    background: ${({ theme }) =>
      theme.palette.mode === 'dark' ? lighten(theme.palette.background.paper, 0.2) : darken(theme.palette.background.paper, 0.2)};

    span {
      text-overflow: clip;
      white-space: normal;
      word-break: break-all;
    }

    .remove {
      display: flex;
    }
  }
`;

const Value = styled.span`
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  width: 100%;
`;
